import { PROCTOR_TYPE_TEXTS } from '@exam-center/proctor-management';
import {
  useGetProctorRequestDownloadSignedUrlWithInfo,
  ProctorRequestFormFields,
} from '@exam-center/proctor-request-management';
import { PHONE_NUMBER_MASK } from '@exam-center/utils';
import { Flex, Text } from '@shared/components';
import { formatDate } from '@shared/utils';
import IMask from 'imask';
import { useMemo } from 'react';

import { useProctorRequestDetails } from '../../providers';
import { getFilePreviewModeByType } from '../../utils/get-file-preview-mode-by-type';

export default function ProctorRequestDetailsReadOnlyFields() {
  const { proctorRequest } = useProctorRequestDetails();

  const maskedPhoneNumber = useMemo(() => {
    const maskedPhoneNumber = IMask.createMask({ mask: PHONE_NUMBER_MASK });
    maskedPhoneNumber.resolve(proctorRequest.phoneNumber ?? '');
    return maskedPhoneNumber;
  }, [proctorRequest.phoneNumber]);

  const agreementFiles = useGetProctorRequestDownloadSignedUrlWithInfo({
    examCenterProctorRequestId: proctorRequest.id,
    proctorRequestStorageObject: proctorRequest?.agreementStorageObjects ?? [],
    options: { skip: !proctorRequest },
  });

  const personalInfoFiles = useGetProctorRequestDownloadSignedUrlWithInfo({
    examCenterProctorRequestId: proctorRequest.id,
    proctorRequestStorageObject: proctorRequest?.personalInfoStorageObjects ?? [],
    options: { skip: !proctorRequest },
  });

  return (
    <Flex sx={{ flex: 1 }}>
      <ProctorRequestFormFields
        fields={{
          proctorType: {
            mode: 'view',
            value: <Text>{PROCTOR_TYPE_TEXTS[proctorRequest.type] ?? '-'}</Text>,
          },
          personal: {
            idNumber: {
              mode: 'view',
              value: proctorRequest.idNumber,
            },
            titleTh: {
              mode: 'view',
              value: proctorRequest.titleTh,
            },
            firstNameTh: {
              mode: 'view',
              value: proctorRequest.firstNameTh,
            },
            lastNameTh: {
              mode: 'view',
              value: proctorRequest.lastNameTh,
            },
            titleEn: {
              mode: 'view',
              value: proctorRequest.titleEn,
            },
            firstNameEn: {
              mode: 'view',
              value: proctorRequest.firstNameEn,
            },
            lastNameEn: {
              mode: 'view',
              value: proctorRequest.lastNameEn,
            },
            email: {
              mode: 'view',
              value: proctorRequest.email,
            },
            phoneNumber: {
              mode: 'view',
              value: maskedPhoneNumber.value || '-',
            },
            address: {
              mode: 'view',
              value: proctorRequest.address || '-',
            },
          },
          employeeInfo: {
            code: {
              mode: 'view',
              value: proctorRequest.metadata?.employeeId || '-',
            },
            position: {
              mode: 'view',
              value: proctorRequest.metadata?.position || '-',
            },
            department: {
              mode: 'view',
              value: proctorRequest.metadata?.department || '-',
            },
            isTerminationAssign: {
              mode: 'view',
              value: <Text>ไม่มีกำหนด</Text>,
            },
            startedAt: {
              mode: 'view',
              value: proctorRequest.startedAt ? formatDate(proctorRequest.startedAt) : '-',
            },
            endedAt: {
              mode: 'view',
              value: proctorRequest.endedAt ? formatDate(proctorRequest.endedAt) : '-',
            },
          },
          documents: {
            agreementForms: {
              mode: 'view',
              slotProps: {
                files: agreementFiles.map((file) => ({
                  ...file,
                  mode: getFilePreviewModeByType(file.type),
                })),
              },
            },
            identifications: {
              mode: 'view',
              slotProps: {
                files: personalInfoFiles.map((file) => ({
                  ...file,
                  mode: getFilePreviewModeByType(file.type),
                })),
              },
            },
          },
        }}
        options={{
          shouldShowEndedAtDateDisplay: !!proctorRequest.endedAt,
          hideIdentificationFiles: false,
        }}
      />
    </Flex>
  );
}
